/**
 * ControlCardEquipmentRepository
 * 
 * Repository for fetching equipment data with specifications for control card generation.
 * Handles:
 * - Main equipment fetching
 * - Sub-equipment relationships
 * - Specification fetching with template metadata
 * - Reference resolution (manufacturers, standards)
 */

import { SupabaseClient } from '@supabase/supabase-js'
import {
  DynamicSpecificationCollection,
  type DatabaseSpecificationRecord,
  type SpecificationTemplate,
} from '../../domain'
import { ReferenceResolutionService } from '../services/ReferenceResolutionService'
import { isFUnitCategoryCode } from '../../../../utils/lxBxH'
import {
  STMIDBEquipmentService,
  STMIDB_WLL_COMPANY_ID,
  mapSTMIDBToWLLCategoryCode,
  type STMIDBEquipment,
} from '../../../../services/api/stmidbEquipmentService'

/**
 * Equipment data for control card (domain model)
 */
export interface ControlCardEquipment {
  id: string
  name: string | null
  assetNumber: string | null
  serialNumber: string | null
  description: string | null
  category: string | null
  categoryId: string | null
  categoryDescription: string | null
  equipmentType: string | null
  ownerName: string | null
  serviceOrder: string | null
  location: string | null
  lastInspectionDate: string | null
  nextInspectionDate: string | null
  manufactureYear: number | null
  checklist: string | null // Norwegian name of the checklist for control card
  specifications: DynamicSpecificationCollection
}

/**
 * Inspection data for control card
 */
export interface ControlCardInspection {
  jobType: string
  performedDate: string
  validUntil?: string
  performedBy: string
  responsiblePerson: string
  controlledAgainst?: string
  status: string
  remarks?: string
  reportNumber?: string
  performedBySignature?: string
  responsiblePersonSignature?: string
}

/**
 * Complete control card data
 */
export interface ControlCardData {
  mainEquipment: ControlCardEquipment
  subEquipment: ControlCardEquipment[]
  inspection: ControlCardInspection
}

/**
 * Result type for repository operations
 */
export type RepositoryResult<T> = 
  | { success: true; data: T }
  | { success: false; error: string }

/**
 * Repository for control card equipment data
 */
export class ControlCardEquipmentRepository {
  private readonly supabase: SupabaseClient
  private templateCache?: Map<string, SpecificationTemplate>
  private referenceResolver: ReferenceResolutionService

  constructor(supabase: SupabaseClient) {
    this.supabase = supabase
    this.referenceResolver = new ReferenceResolutionService(supabase)
  }

  /**
   * Fetch complete equipment data for control card generation
   */
  async getEquipmentForControlCard(
    equipmentId: string
  ): Promise<RepositoryResult<ControlCardEquipment>> {
    try {
      // Load specification templates if not already cached
      if (!this.templateCache) {
        await this.loadTemplateMetadata()
      }

      // Fetch main equipment — try WLL first, then fall back to STMIDB for
      // SLB rows that only exist on the customer's Supabase project. The
      // __stmidb flag tells us which spec store (and sub-equipment source)
      // to use downstream.
      let mainEquipment = await this.fetchMainEquipment(equipmentId)
      let isSTMIDB = false
      if (!mainEquipment) {
        mainEquipment = await this.fetchSTMIDBMainEquipment(equipmentId)
        isSTMIDB = !!mainEquipment
      }
      if (!mainEquipment) {
        return {
          success: false,
          error: `Equipment not found: ${equipmentId}`,
        }
      }

      // Fetch checklist for category
      const categoryId = mainEquipment.category?.id || null
      const checklist = categoryId 
        ? await this.fetchCategoryChecklist(categoryId) 
        : null

      // Fetch and resolve specifications. SLB specs round-trip through
      // external_equipment_specifications; WLL specs through
      // equipment_specifications_v2.
      const dbSpecs = isSTMIDB
        ? await this.fetchExternalSpecifications(equipmentId)
        : await this.fetchSpecifications(equipmentId)
      const resolvedSpecs = await this.resolveSpecificationReferences(dbSpecs)
      
      // Create synthetic specifications from equipment properties
      const syntheticSpecs = this.createSyntheticSpecifications(mainEquipment)
      
      // Combine synthetic specs (first) with regular specs
      const categoryCode = mainEquipment.category?.code || null
      const resolvedForCard = isFUnitCategoryCode(categoryCode)
        ? resolvedSpecs.filter(s => s.spec_key !== 'wll' && s.spec_key !== 'wll_at')
        : resolvedSpecs
      const allSpecs = [...syntheticSpecs, ...resolvedForCard]
      
      const specifications = DynamicSpecificationCollection.fromDatabaseResults(
        allSpecs,
        this.templateCache
      )

      // Build domain model
      const equipment: ControlCardEquipment = {
        id: mainEquipment.id,
        name: mainEquipment.name,
        assetNumber: mainEquipment.asset_number,
        serialNumber: mainEquipment.serial_number,
        description: mainEquipment.description,
        category: mainEquipment.category?.code || null,
        categoryId: categoryId,
        categoryDescription: mainEquipment.category?.title_en || null,
        equipmentType: mainEquipment.model || mainEquipment.equipment_type,
        ownerName: mainEquipment.company?.name || null,
        serviceOrder: mainEquipment.service_order || null,
        location: mainEquipment.location,
        lastInspectionDate: mainEquipment.last_inspection_date,
        nextInspectionDate: mainEquipment.next_inspection_date,
        manufactureYear: mainEquipment.manufacture_year,
        checklist: checklist,
        specifications,
      }

      return {
        success: true,
        data: equipment,
      }
    } catch (error) {
      console.error('Error fetching equipment for control card:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      }
    }
  }

  /**
   * Fetch equipment with sub-equipment for complete control card
   */
  async getCompleteControlCardData(
    equipmentId: string
  ): Promise<RepositoryResult<Omit<ControlCardData, 'inspection'>>> {
    try {
      // Fetch main equipment
      const mainResult = await this.getEquipmentForControlCard(equipmentId)
      if (!mainResult.success) {
        return mainResult as RepositoryResult<Omit<ControlCardData, 'inspection'>>
      }

      // Fetch sub-equipment
      const subEquipmentResult = await this.getSubEquipment(equipmentId)
      const subEquipment = subEquipmentResult.success ? subEquipmentResult.data : []

  
      return {
        success: true,
        data: {
          mainEquipment: mainResult.data,
          subEquipment,
        },
      }
    } catch (error) {
      console.error('Error fetching complete control card data:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      }
    }
  }

  /**
   * Fetch sub-equipment for main equipment. Resolves against
   * equipment_sub_equipment_v2 first; if no rows match (the parent is SLB,
   * or the mapping just doesn't exist), falls back to STMIDB's parent/child
   * relationship via get_stmidb_sub_equipment so SLB control cards get the
   * same treatment as WLL ones.
   */
  async getSubEquipment(
    mainEquipmentId: string
  ): Promise<RepositoryResult<ControlCardEquipment[]>> {
    try {
      const { data: relationships, error: relError } = await this.supabase
        .from('equipment_sub_equipment_v2')
        .select('child_equipment_id')
        .eq('main_equipment_id', mainEquipmentId)

      if (relError) {
        console.error('Error fetching sub-equipment relationships:', relError)
        return {
          success: false,
          error: relError.message,
        }
      }

      let childIds: string[] = ((relationships || []) as Array<{ child_equipment_id: string }>)
        .map(r => r.child_equipment_id)
        .filter(Boolean)

      if (childIds.length === 0) {
        try {
          const stmidbChildren = await STMIDBEquipmentService.getSubEquipment(
            mainEquipmentId
          )
          childIds = (stmidbChildren || []).map(c => c.id).filter(Boolean)
        } catch (err) {
          // Not fatal — WLL parents simply won't have STMIDB children, and
          // the RPC will return []. Log and continue with an empty list.
          console.warn('STMIDB sub-equipment lookup failed:', err)
        }
      }

      if (childIds.length === 0) {
        return {
          success: true,
          data: [],
        }
      }

      const subEquipment: ControlCardEquipment[] = []
      for (const childId of childIds) {
        const result = await this.getEquipmentForControlCard(childId)
        if (result.success) {
          subEquipment.push(result.data)
        } else {
          console.warn(`Failed to fetch sub-equipment ${childId}:`, result.error)
        }
      }

      return {
        success: true,
        data: subEquipment,
      }
    } catch (error) {
      console.error('Error fetching sub-equipment:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      }
    }
  }

  /**
   * Create synthetic specifications from equipment properties
   * These always appear first if they have values
   */
  private createSyntheticSpecifications(equipment: any): DatabaseSpecificationRecord[] {
    const syntheticSpecs: DatabaseSpecificationRecord[] = []

    // Priority -3, -2, -1 ensures these appear first
    const propertyMappings = [
      { 
        key: 'owner_id', 
        value: equipment.owner_id, 
        label: 'Eiers ID/Owner ID',
        priority: -3 
      },
      { 
        key: 'serial_number', 
        value: equipment.serial_number, 
        label: 'Serienr./Serial No.',
        priority: -2 
      },
      { 
        key: 'part_no', 
        value: equipment.part_number, 
        label: 'Part No.',
        priority: -1 
      },
    ]

    for (const mapping of propertyMappings) {
      // Only add if value exists
      if (mapping.value) {
        syntheticSpecs.push({
          spec_key: mapping.key,
          value_text: String(mapping.value),
          value_numeric: null,
          value_integer: null,
          value_date: null,
          value_boolean: null,
          value_reference_id: null,
          template: {
            label_en: mapping.label,
            label_no: mapping.label,
            data_type: 'text',
            reference_table: null,
            // Add display_order for priority
            display_order: mapping.priority,
          } as any,
          unit: null,
        })
      }
    }

    if (syntheticSpecs.length > 0) {
        syntheticSpecs.forEach(spec => {
          })
    }
    return syntheticSpecs
  }

  /**
   * Fetch main equipment record from database. Returns null when the row
   * doesn't exist in equipment_v2 (so callers can fall back to STMIDB).
   */
  private async fetchMainEquipment(equipmentId: string) {
    const { data, error } = await this.supabase
      .from('equipment_v2')
      .select(`
        *,
        company:companies(name),
        category:categories_v2(id, code, title_en)
      `)
      .eq('id', equipmentId)
      .maybeSingle()

    if (error) {
      console.error('Error fetching main equipment:', error)
      throw error
    }

    return data
  }

  /**
   * Fetch an equipment row from STMIDB and reshape it to the same object
   * layout the WLL fetchMainEquipment returns, so the rest of the
   * control-card pipeline doesn't need to branch at every field access.
   *
   * category is resolved by matching STMIDB's top-level code (e.g. "F1")
   * against categories_v2.code — STMIDB only persists top-level categories
   * per the project convention, so there's no F1-1 disambiguation to do.
   */
  private async fetchSTMIDBMainEquipment(
    equipmentId: string
  ): Promise<Record<string, any> | null> {
    const stmidb: STMIDBEquipment | null =
      await STMIDBEquipmentService.getEquipmentById(equipmentId)
    if (!stmidb) return null

    const wllCategoryCode = mapSTMIDBToWLLCategoryCode(
      stmidb.category,
      stmidb.equipment_type
    )

    let category: { id: string; code: string; title_en: string | null } | null = null
    if (wllCategoryCode) {
      const { data: catRow } = await this.supabase
        .from('categories_v2')
        .select('id, code, title_en')
        .eq('code', wllCategoryCode)
        .maybeSingle()
      if (catRow) {
        category = catRow as { id: string; code: string; title_en: string | null }
      }
    }

    let companyName: string | null = stmidb.owner_name || null
    if (!companyName) {
      const { data: companyRow } = await this.supabase
        .from('companies')
        .select('name')
        .eq('id', STMIDB_WLL_COMPANY_ID)
        .maybeSingle()
      companyName = (companyRow as { name?: string } | null)?.name || null
    }

    return {
      id: stmidb.id,
      name: stmidb.description || null,
      asset_number: stmidb.asset_number,
      serial_number: stmidb.serial_number,
      description: stmidb.description,
      model: null,
      equipment_type: stmidb.equipment_type,
      // "Owner ID"/EQ in ECM maps to STMIDB's equipment_code (see
      // mapSTMIDBToWLLEquipment). The synthetic-spec builder reads
      // owner_id + part_number + serial_number off this object.
      owner_id: stmidb.equipment_code || null,
      part_number: stmidb.part_number,
      service_order: null,
      location: null,
      last_inspection_date: stmidb.last_inspection_date,
      next_inspection_date: stmidb.next_inspection_date,
      manufacture_year: null,
      category,
      company: companyName ? { name: companyName } : null,
      __stmidb: true,
    }
  }

  /**
   * Fetch specifications for an SLB/external equipment row. These live in
   * the app's external_equipment_specifications table (not STMIDB itself)
   * and are addressed by (external_equipment_id, source='stmidb'). The RPC
   * already joins label_en/label_no/data_type from
   * canonical_specification_templates; reference_table comes from the
   * repository's templateCache since the RPC doesn't return it.
   */
  private async fetchExternalSpecifications(
    equipmentId: string
  ): Promise<DatabaseSpecificationRecord[]> {
    const specs = await STMIDBEquipmentService.getExternalEquipmentSpecs(equipmentId)
    const unitIds = Array.from(
      new Set(
        specs
          .map(s => s.unit_id)
          .filter((u): u is string => !!u)
      )
    )
    let unitMap = new Map<string, string>()
    if (unitIds.length > 0) {
      const { data: units } = await this.supabase
        .from('units_v2')
        .select('id, symbol')
        .in('id', unitIds)
      if (units) {
        unitMap = new Map(
          (units as Array<{ id: string; symbol: string | null }>).map(u => [
            u.id,
            u.symbol || '',
          ])
        )
      }
    }

    return specs.map<DatabaseSpecificationRecord>(s => {
      const template = this.templateCache?.get(s.spec_key)
      return {
        spec_key: s.spec_key,
        value_text: s.value_text,
        value_numeric: s.value_numeric,
        value_integer: s.value_integer,
        value_date: s.value_date,
        value_boolean: s.value_boolean,
        value_reference_id: s.value_reference_id,
        template: {
          label_en: s.label_en || template?.label_en || undefined,
          label_no: s.label_no || template?.label_no || undefined,
          data_type: s.data_type || template?.data_type || 'text',
          reference_table: template?.reference_table || undefined,
        },
        unit: s.unit_id ? { symbol: unitMap.get(s.unit_id) || '' } : null,
      }
    })
  }

  /**
   * Fetch the default checklist for a category
   */
  private async fetchCategoryChecklist(categoryId: string): Promise<string | null> {
    try {
      // Get the default checklist (is_default = true) or the first one
      const { data, error } = await this.supabase
        .from('category_checklists')
        .select(`
          checklist:checklists(name_no)
        `)
        .eq('category_id', categoryId)
        .order('is_default', { ascending: false })
        .limit(1)
        .maybeSingle()

      if (error) {
        console.error('Error fetching category checklist:', error)
        return null
      }

      // Extract the Norwegian name from the nested checklist object
      return (data?.checklist as any)?.name_no || null
    } catch (error) {
      console.error('Error in fetchCategoryChecklist:', error)
      return null
    }
  }

  /**
   * Fetch specifications for equipment
   */
  private async fetchSpecifications(
    equipmentId: string
  ): Promise<DatabaseSpecificationRecord[]> {
    
    const { data, error } = await this.supabase
      .from('equipment_specifications_v2')
      .select(`
        spec_key,
        value_text,
        value_numeric,
        value_integer,
        value_date,
        value_boolean,
        value_reference_id,
        template:canonical_specification_templates!template_id (
          label_en,
          label_no,
          data_type,
          reference_table
        ),
        unit:units_v2!unit_id (
          symbol
        )
      `)
      .eq('equipment_id', equipmentId)

    if (error) {
      console.error('Error fetching specifications:', error)
      throw error
    }

    // Supabase types nested embeds as arrays even for 1:1 FK joins; the
    // downstream code treats `template`/`unit` as singular objects.
    return (data || []) as unknown as DatabaseSpecificationRecord[]
  }

  /**
   * Resolve reference values in specifications
   */
  private async resolveSpecificationReferences(
    specs: DatabaseSpecificationRecord[]
  ): Promise<DatabaseSpecificationRecord[]> {
    const resolved: DatabaseSpecificationRecord[] = []


    for (const spec of specs) {
      const template = spec.template as any
      const referenceTable = template?.reference_table

      // Check if value_reference_id needs resolution
      if (spec.value_reference_id) {
            
        const resolvedValue = await this.referenceResolver.resolveReference(
          spec.value_reference_id,
          spec.spec_key,
          referenceTable
        )
        
            
        // Store resolved value in value_text for downstream processing
        resolved.push({
          ...spec,
          value_text: resolvedValue,
          value_reference_id: null, // Clear the reference ID
        })
      }
      // Check if value_text contains UUID(s). Reference fields such as
      // prod_acc_to are persisted as a JSON array string (e.g.
      // '["<uuid>","<uuid>"]'); others may be a single UUID or a
      // comma-separated list. Normalise all of these to a list of parts.
      else if (spec.value_text) {
        const parts = this.parseReferenceParts(spec.value_text)
        const uuids = parts.filter((p: string) => ReferenceResolutionService.isUUID(p))

        if (uuids.length > 0) {
          const refType = this.referenceResolver.detectReferenceType(spec.spec_key, referenceTable)

          if (refType === 'standard' && uuids.length > 1) {
            const codeOnly = spec.spec_key.toLowerCase() === 'prod_acc_to'
            const resolvedMap = await this.referenceResolver.resolveStandards(uuids, codeOnly)
            const resolvedParts = uuids.map(id => resolvedMap.get(id) || id)
            resolved.push({ ...spec, value_text: resolvedParts.join(', ') })
          } else if (uuids.length === 1 && parts.length === 1) {
            const resolvedValue = await this.referenceResolver.resolveReference(
              uuids[0], spec.spec_key, referenceTable
            )
            resolved.push({ ...spec, value_text: resolvedValue })
          } else {
            resolved.push(spec)
          }
        } else {
          resolved.push(spec)
        }
      }
      // No resolution needed
      else {
        resolved.push(spec)
      }
    }

    return resolved
  }

  /**
   * Normalise a value_text into a list of reference parts.
   *
   * Reference specifications (e.g. prod_acc_to) are stored as a JSON array
   * string such as '["<uuid>","<uuid>"]'. Legacy / other fields may be a
   * bare value or a comma-separated list. This returns the trimmed,
   * non-empty parts for either encoding.
   */
  private parseReferenceParts(valueText: string): string[] {
    const out: string[] = []

    const visit = (value: unknown, depth: number): void => {
      if (value == null || depth > 6) return

      if (Array.isArray(value)) {
        value.forEach(item => visit(item, depth + 1))
        return
      }

      if (typeof value !== 'string') return

      const s = value.trim()
      if (!s) return

      // Unwrap JSON-encoded arrays or quoted strings. Some legacy rows are
      // double-encoded (e.g. '["\"<uuid>\""]'), so recurse until we reach
      // bare values.
      if (s.startsWith('[') || s.startsWith('"')) {
        try {
          visit(JSON.parse(s), depth + 1)
          return
        } catch {
          // Not valid JSON — fall through to comma handling below.
        }
      }

      if (s.includes(',')) {
        s.split(',').forEach(part => visit(part, depth + 1))
        return
      }

      const cleaned = s.replace(/^["']+|["']+$/g, '').trim()
      if (cleaned) out.push(cleaned)
    }

    visit(valueText, 0)
    return Array.from(new Set(out))
  }

  /**
   * Load specification template metadata for labels and priorities
   */
  private async loadTemplateMetadata(): Promise<void> {
    
    const { data: templates, error } = await this.supabase
      .from('canonical_specification_templates')
      .select('spec_key, label_no, label_en, data_type, display_order, reference_table')

    if (error) {
      console.error('Error loading template metadata:', error)
      this.templateCache = new Map()
      return
    }

    this.templateCache = new Map(
      (templates as SpecificationTemplate[] | null)?.map(t => [t.spec_key, t]) || []
    )

  }

  /**
   * Clear template cache (useful for testing or when templates change)
   */
  clearTemplateCache(): void {
    this.templateCache = undefined
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): {
    templates: number
    references: { manufacturers: number; standards: number; total: number }
  } {
    return {
      templates: this.templateCache?.size || 0,
      references: this.referenceResolver.getCacheStats(),
    }
  }
}

